import { Amplify } from "aws-amplify";

const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
const COGNITO_USER_POOL_ID = import.meta.env
    .VITE_COGNITO_USER_POOL_ID as string;
const VITE_COGNITO_ENDPOINT = import.meta.env.VITE_COGNITO_ENDPOINT as string;

// Client-side mirror of the Cognito password policy (infrastructure/core/cognito.tf).
export const passwordPolicy = {
    minLength: 8,
    requireLowercase: true,
    requireUppercase: true,
    requireNumbers: true,
    requireSpecialCharacters: true,
};

/**
 * The host a build is served from: `app` for the customer app, `ba` for the
 * admin console. Each entry point passes its own, so the OAuth redirect lands
 * back on the origin the user started from and neither bundle carries the
 * other's hostname. Whatever is passed here must also be in the client's
 * callback_urls (infrastructure/core/cognito.tf).
 */
export type AmplifyHost = "app" | "ba";

export function configureAmplify(host: AmplifyHost): void {
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId: COGNITO_USER_POOL_ID,
                userPoolClientId: COGNITO_CLIENT_ID,
                identityPoolId: "",
                loginWith: {
                    username: true,
                    oauth: {
                        // The user pool's own host (infrastructure/core/cognito.tf).
                        // Nobody sees it: the sign-in screens are ours, and Cognito
                        // only takes over for the redirect out to Google and the
                        // bounce back. It is on our domain rather than Aws's because
                        // a Saml customer's IdP posts assertions here, and that
                        // address cannot be changed once their IT admin has entered
                        // it.
                        domain: `auth.${import.meta.env.VITE_DOMAIN}`,
                        scopes: [
                            "email",
                            "openid",
                            "profile",
                            "aws.cognito.signin.user.admin",
                        ],
                        redirectSignIn: [
                            `https://${host}.${import.meta.env.VITE_DOMAIN}`,
                        ],
                        redirectSignOut: [
                            `https://${import.meta.env.VITE_DOMAIN}`,
                        ],
                        responseType: "code",
                    },
                },
                userPoolEndpoint: VITE_COGNITO_ENDPOINT,
                signUpVerificationMethod: "code",
                userAttributes: {
                    email: { required: true },
                    name: { required: true },
                    picture: { required: false },
                },
                allowGuestAccess: false,
                passwordFormat: passwordPolicy,
            },
        },
    });
}
